import Page from "../../common/page/Page"
import Loader from "../../common/loader/Loader"
import style from "./OrderSummary.module.css"
import { useEffect, useState } from "react"
import axios from "axios"
import { useParams, useNavigate } from "react-router-dom"
import { getOrderStatusLabel } from "../../common/utils/StatusConvert"
import { useAuth } from "../../data/useAuth"
import { decodeHtml } from "../../common/utils/address.util"

const SingleOrderGeneral = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { signOut, user } = useAuth()

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true)
      try {
        const { data } = await axios.get(`/api/orders/${id}`)
        setOrder(data)
      } catch (err) {
        if (err.response?.status === 401) {
          signOut()
        }
        setError(err.response?.data?.message || err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchOrder()
  }, [id])

  if (loading) {
    return (
      <Page title="Order Details" searchDisabled is_admin={user?.is_admin}>
        <div style={{ height: "calc(100svh - 140px)", display: "flex" }}>
          <div style={{ margin: "auto" }}>
            <Loader />
          </div>
        </div>
      </Page>
    )
  }

  if (error) {
    return (
      <Page title="Order Details" searchDisabled is_admin={user?.is_admin}>
        <div className={style.checkoutContainer}>
          <div className={style.error}>{error}</div>
        </div>
      </Page>
    )
  }

  if (!order) return null

  const {
    id: orderId,
    name,
    line_1,
    line_2,
    city,
    district,
    state,
    country,
    pin_code,
    phone_number,
    order_products = [],
    order_statuses = [],
    amount,
    gst,
    delivery_charge,
    payment_gateway_charge,
    razorpay_order_id,
    created_at,
  } = order

  const latestStatus = order_statuses[0]
  const statusDetails = latestStatus
    ? getOrderStatusLabel(latestStatus.status)
    : { heading: "Placed", message: "Your order has been placed.", color: "#2563eb" }

  const totalAmount =
    parseFloat(amount || 0) +
    parseFloat(gst || 0) +
    parseFloat(delivery_charge || 0) +
    parseFloat(payment_gateway_charge || 0)

  const formattedDate = created_at
    ? new Date(created_at).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : ""

  return (
    <Page title={`Order #${orderId}`} searchDisabled is_admin={user?.is_admin}>
      <div className={style.checkoutContainer}>
        {/* Header Banner Card with Order ID & Status */}
        <div className={style.orderHeaderCard}>
          <div>
            <div className={style.orderIdBadge}>Order #{orderId}</div>
            {formattedDate && <p className={style.orderDateText}>Placed on {formattedDate}</p>}
          </div>
          <div
            className={style.statusBadge}
            style={{
              backgroundColor: `${statusDetails.color}15`,
              color: statusDetails.color,
              borderColor: `${statusDetails.color}40`,
            }}
          >
            <span
              className={style.statusDot}
              style={{ backgroundColor: statusDetails.color }}
            />
            {statusDetails.heading}
          </div>
        </div>

        {/* Status Message Alert Card */}
        {statusDetails && (
          <div
            className={style.statusAlertCard}
            style={{
              borderLeftColor: statusDetails.color,
            }}
          >
            <h4 style={{ color: statusDetails.color, margin: "0 0 4px 0", fontSize: "0.95rem" }}>
              {statusDetails.heading}
            </h4>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "#475569" }}>
              {statusDetails.message}
            </p>
            {razorpay_order_id && (
              <p style={{ margin: "6px 0 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
                Payment Ref: {razorpay_order_id}
              </p>
            )}
          </div>
        )}

        <div className={style.checkoutGrid}>
          {/* Main Column: Delivery Address & Ordered Items */}
          <div className={style.mainColumn}>
            {/* Step 1: Delivery Address */}
            <section className={style.cardSection}>
              <div className={style.sectionHeader}>
                <span className={style.stepBadge}>1</span>
                <h3>Delivery Address</h3>
              </div>
              <div className={style.modernAddressCard}>
                <h4 className={style.addressRecipient}>{decodeHtml(name)}</h4>
                <p className={style.addressText}>
                  {[decodeHtml(line_1), decodeHtml(line_2)].filter(Boolean).join(", ")}
                </p>
                <p className={style.addressText}>
                  {decodeHtml(city)}, {decodeHtml(district)}, {decodeHtml(state)} - {pin_code}
                </p>
                <p className={style.addressText}>{decodeHtml(country)}</p>
                <div className={style.phoneChip}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  {phone_number}
                </div>
              </div>
            </section>

            {/* Step 2: Items Ordered */}
            <section className={style.cardSection}>
              <div className={style.sectionHeader}>
                <span className={style.stepBadge}>2</span>
                <h3>Items Ordered ({order_products.length})</h3>
              </div>
              <div className={style.productList}>
                {order_products.map(({ Product, qty, amount: itemAmount, gst: itemGst }, index) => {
                  const itemRate = parseFloat(itemAmount || 0)
                  const itemGstRate = parseFloat(itemGst || 0)
                  const itemTotal = (itemRate + itemGstRate) * qty
                  return (
                    <div key={index} className={style.productItemCard}>
                      <div className={style.productImage}>
                        <img
                          src={`${import.meta.env.VITE_BACKEND_URL}/public/images/products/${
                            Product?.product_images?.[0]?.image_url
                          }`}
                          alt={Product?.name || "product"}
                        />
                      </div>
                      <div className={style.productDetails}>
                        <h4 className={style.productTitle}>{decodeHtml(Product?.name)}</h4>
                        <div className={style.productActionRow}>
                          <div className={style.qtyRow}>
                            <span className={style.qtyLabel}>Quantity:</span>
                            <span className={style.metaQty}>{qty}</span>
                          </div>
                          <div className={style.productPriceBox}>
                            <span className={style.unitPrice}>₹{itemRate.toFixed(2)} each</span>
                            <span className={style.totalItemPrice}>₹{itemTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          </div>

          {/* Step 3: Sidebar Order Payment Summary */}
          <div className={style.sidebarColumn}>
            <div className={style.summaryCard}>
              <div className={style.sectionHeader}>
                <span className={style.stepBadge}>3</span>
                <h3>Payment Breakdown</h3>
              </div>
              <div className={style.summaryRows}>
                <div className={style.summaryRow}>
                  <span>Items Subtotal</span>
                  <span>₹{parseFloat(amount || 0).toFixed(2)}</span>
                </div>
                {parseFloat(gst || 0) > 0 && (
                  <div className={style.summaryRow}>
                    <span>GST (Applicable)</span>
                    <span>₹{parseFloat(gst || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className={style.summaryRow}>
                  <span>Delivery Charges</span>
                  <span>
                    {parseFloat(delivery_charge || 0) === 0 ? (
                      <strong style={{ color: "#16a34a" }}>FREE</strong>
                    ) : (
                      `₹${parseFloat(delivery_charge || 0).toFixed(2)}`
                    )}
                  </span>
                </div>
                {parseFloat(payment_gateway_charge || 0) > 0 && (
                  <div className={style.summaryRow}>
                    <span>Transaction Fee</span>
                    <span>₹{parseFloat(payment_gateway_charge || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className={`${style.summaryRow} ${style.grandTotalRow}`}>
                  <span>Total Paid</span>
                  <span className={style.grandTotalAmount}>₹{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <div style={{ marginTop: "12px" }}>
                <button
                  type="button"
                  onClick={() => navigate("/orders")}
                  className={style.addAddressOutlineBtn}
                >
                  ← Back to My Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}

export default SingleOrderGeneral
